// Liquidaciones de beneficios sociales: décimo tercero (Art. 111), décimo
// cuarto (Art. 113) y utilidades (Art. 97). Nómina → Beneficios Sociales.
// El motor vive en la BD (nom_beneficio_generar — script 163); ciclo
// borrador → cerrado → pagado como el rol.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, BookOpen, Loader2, Lock, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fieldCls, money, nomRpc, useDialogoEnLinea, type BeneficioData, type BeneficioHeader, hoyISO } from './shared';
import { EstadoRol } from './Rol';
import { fechaLocal } from '@/lib/utils/fecha';

interface Cfg {
  tipo: BeneficioHeader['tipo'];
  titulo: string;
  leyenda: string;
  conMonto?: boolean;
  columnas: { base?: string; dias?: boolean; cargas?: boolean; yaPagado?: boolean; cincoPct?: boolean };
}

function Liquidacion({ cfg }: { cfg: Cfg }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [periodo, setPeriodo] = useState(new Date().getFullYear());
  const [monto, setMonto] = useState('');
  const [q, setQ] = useState('');

  const { data: lista = [] } = useQuery<BeneficioHeader[]>({
    queryKey: ['nom-beneficios', cfg.tipo],
    queryFn: () => nomRpc<BeneficioHeader[]>('nom_beneficios', { _tipo: cfg.tipo }),
    staleTime: 0,
  });
  const actual = lista.find((b) => b.periodo === periodo) ?? null;

  const { data: detalle, isFetching } = useQuery<BeneficioData>({
    queryKey: ['nom-beneficio', actual?.id],
    queryFn: () => nomRpc<BeneficioData>('nom_beneficio', { _id: actual!.id }),
    enabled: !!actual,
    staleTime: 0,
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['nom-beneficios'] });
    void qc.invalidateQueries({ queryKey: ['nom-beneficio'] });
  };
  const generar = useMutation({
    mutationFn: () => nomRpc<BeneficioData>('nom_beneficio_generar', {
      _tipo: cfg.tipo, _periodo: periodo, _monto: cfg.conMonto ? Number(monto) || null : null,
    }),
    onSuccess: () => { toast.success('Liquidación generada (borrador)'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: () => nomRpc('nom_beneficio_cerrar', { _id: actual!.id }),
    onSuccess: () => { toast.success('Liquidación cerrada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pagar = useMutation({
    mutationFn: () => nomRpc('nom_beneficio_pagar', { _id: actual!.id, _fecha: hoyISO() }),
    onSuccess: () => { toast.success('Liquidación marcada como pagada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: () => nomRpc('nom_beneficio_eliminar', { _id: actual!.id }),
    onSuccess: () => { toast.success('Borrador eliminado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const contabilizar = useMutation({
    mutationFn: () => nomRpc<{ numero: number; reemplazado: boolean }>('nom_beneficio_contabilizar', { _id: actual!.id }),
    onSuccess: (r) => {
      toast.success(`Asiento contable N.º ${r.numero} ${r.reemplazado ? 'regenerado' : 'creado'} (Contabilidad → Asientos)`);
      invalidar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const t = q.trim().toLowerCase();
  const detalles = (detalle?.detalles ?? []).filter((d) =>
    !t || d.razon_social.toLowerCase().includes(t) || d.identificacion.includes(t));

  type DetalleRow = (typeof detalles)[number];
  const colsExp: { header: string; get: (d: DetalleRow) => Celda; money?: boolean; sum?: boolean }[] = [
    { header: 'Código', get: (d) => d.empleado_codigo },
    { header: 'Empleado', get: (d) => d.razon_social },
  ];
  if (cfg.columnas.base) colsExp.push({ header: cfg.columnas.base, get: (d) => Number(d.base), money: true, sum: true });
  if (cfg.columnas.dias) colsExp.push({ header: 'Días', get: (d) => (d.dias != null ? Number(d.dias) : ''), sum: true });
  if (cfg.columnas.cargas) colsExp.push({ header: 'Cargas', get: (d) => d.cargas ?? 0, sum: true });
  if (cfg.columnas.yaPagado) colsExp.push({ header: 'Ya pagado (mens.)', get: (d) => Number(d.ya_pagado), money: true, sum: true });
  colsExp.push({ header: cfg.columnas.cincoPct ? '10% tiempo' : 'Valor', get: (d) => Number(d.valor), money: true, sum: true });
  if (cfg.columnas.cincoPct) colsExp.push({ header: '5% cargas', get: (d) => Number(d.valor_cargas), money: true, sum: true });
  if (cfg.columnas.cincoPct) colsExp.push({ header: 'Total', get: (d) => Number(d.total), money: true, sum: true });

  const headersExp = ['N°', ...colsExp.map((c) => c.header)];
  const MONEYCOLS = colsExp.map((c, i) => (c.money ? i + 1 : -1)).filter((i) => i >= 0);
  const colsDerecha = [0, ...colsExp.map((c, i) => (c.money || c.sum ? i + 1 : -1)).filter((i) => i >= 0)];
  const filasExport: Celda[][] = [
    ...detalles.map((d, i): Celda[] => [i + 1, ...colsExp.map((c) => c.get(d))]),
    ['', '', 'TOTAL', ...colsExp.slice(2).map((c) => (c.sum ? detalles.reduce((s, d) => s + Number(c.get(d) || 0), 0) : ''))],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">{cfg.titulo}</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`${cfg.tipo.replace(/_/g, '-')}-${fechaLocal()}`}
            titulo={cfg.titulo} headers={headersExp} filas={filasExport} colsDerecha={colsDerecha}
            disabled={detalles.length === 0}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
          />
          <input type="number" className={`${fieldCls} w-24`} value={periodo} onChange={(e) => setPeriodo(Number(e.target.value))} aria-label="Período (año)" />
          {actual && <EstadoRol estado={actual.estado} />}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{cfg.leyenda}</p>

      <div className="flex flex-wrap items-end gap-2">
        {cfg.conMonto && (!actual || actual.estado === 'borrador') && (
          <div>
            <div className="text-xs text-muted-foreground mb-1">Utilidad líquida a repartir (15%) ($)</div>
            <input type="number" min="0" step="0.01" className={`${fieldCls} w-52`} value={monto} onChange={(e) => setMonto(e.target.value)} placeholder={actual?.monto_utilidad ? String(actual.monto_utilidad) : ''} />
          </div>
        )}
        {!actual && (
          <Button isLoading={generar.isPending} disabled={cfg.conMonto && !Number(monto)} onClick={() => generar.mutate()}>
            Generar liquidación {periodo}
          </Button>
        )}
        {actual?.estado === 'borrador' && (
          <>
            <Button variant="outline" isLoading={generar.isPending} onClick={() => generar.mutate()}><RefreshCw className="w-4 h-4 mr-1" /> Recalcular</Button>
            <Button isLoading={cerrar.isPending} onClick={async () => { if (await dlg.confirmar('¿Cerrar la liquidación?', { detalle: 'Ya no podrá regenerarse.', aceptar: 'Cerrar' })) cerrar.mutate(); }}><Lock className="w-4 h-4 mr-1" /> Cerrar</Button>
            <Button variant="destructive" isLoading={eliminar.isPending} onClick={async () => { if (await dlg.confirmar('¿Eliminar el borrador?', { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(); }}><Trash2 className="w-4 h-4 mr-1" /> Eliminar</Button>
          </>
        )}
        {actual?.estado === 'cerrado' && (
          <Button isLoading={pagar.isPending} onClick={async () => { if (await dlg.confirmar('¿Marcar como pagada?', { aceptar: 'Marcar pagada' })) pagar.mutate(); }}><Banknote className="w-4 h-4 mr-1" /> Marcar pagada</Button>
        )}
        {actual && actual.estado !== 'borrador' && (
          <>
            <Button variant="outline" isLoading={contabilizar.isPending}
              onClick={async () => {
                const ya = !!detalle?.asiento;
                const criterio = cfg.tipo === 'utilidades'
                  ? 'Se registrará como gasto de utilidades contra utilidades por pagar.'
                  : 'Se consumirá la provisión (provisiones por pagar → décimos por pagar).';
                const ok = await dlg.confirmar(ya ? '¿Regenerar el asiento?' : '¿Contabilizar la liquidación?', {
                  detalle: ya
                    ? `Ya está contabilizada en el asiento N.º ${detalle!.asiento!.numero}. Regenerarlo reemplaza el asiento.`
                    : `${criterio} Requiere el mapeo de cuentas en Contabilidad → Configuración Contable.`,
                  aceptar: ya ? 'Regenerar' : 'Contabilizar',
                });
                if (ok) contabilizar.mutate();
              }}>
              <BookOpen className="w-4 h-4 mr-1" /> {detalle?.asiento ? 'Re-contabilizar' : 'Contabilizar'}
            </Button>
            {detalle?.asiento && (
              <span className="self-center px-2 py-0.5 rounded-full text-xs bg-completado-soft text-completado">
                Asiento N.º {detalle.asiento.numero}
              </span>
            )}
          </>
        )}
        {actual?.estado === 'pagado' && actual.fecha_pagado && (
          <div className="text-sm text-muted-foreground">Pagada el {actual.fecha_pagado}</div>
        )}
      </div>

      {actual && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">Empleados</div><div className="text-lg font-semibold font-mono">{actual.n_empleados}</div></CardContent></Card>
          {cfg.conMonto && actual.monto_utilidad != null && (
            <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">Utilidad repartida (15%)</div><div className="text-lg font-semibold font-mono tabular-nums">{money(Number(actual.monto_utilidad) * 0.15)}</div></CardContent></Card>
          )}
          <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">Total a pagar</div><div className="text-lg font-semibold font-mono tabular-nums">{money(actual.total)}</div></CardContent></Card>
        </div>
      )}

      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}

      {detalle?.beneficio && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Detalle ({detalles.length})</CardTitle>
            <input className={`${fieldCls} w-64`} placeholder="Filtrar nombre o cédula…" value={q} onChange={(e) => setQ(e.target.value)} />
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th>
                {cfg.columnas.base && <th className="py-2 pr-3 text-right">{cfg.columnas.base}</th>}
                {cfg.columnas.dias && <th className="py-2 pr-3 text-right">Días</th>}
                {cfg.columnas.cargas && <th className="py-2 pr-3 text-right">Cargas</th>}
                {cfg.columnas.yaPagado && <th className="py-2 pr-3 text-right">Ya pagado (mens.)</th>}
                <th className="py-2 pr-3 text-right">{cfg.columnas.cincoPct ? '10% tiempo' : 'Valor'}</th>
                {cfg.columnas.cincoPct && <th className="py-2 pr-3 text-right">5% cargas</th>}
                {cfg.columnas.cincoPct && <th className="py-2 pr-3 text-right">Total</th>}
              </tr></thead>
              <tbody>
                {detalles.map((d) => (
                  <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 font-mono">{d.empleado_codigo}</td>
                    <td className="py-2 pr-3">{d.razon_social}<div className="text-xs text-muted-foreground font-mono">{d.identificacion}</div></td>
                    {cfg.columnas.base && <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d.base)}</td>}
                    {cfg.columnas.dias && <td className="py-2 pr-3 text-right font-mono tabular-nums">{d.dias != null ? Number(d.dias) : '—'}</td>}
                    {cfg.columnas.cargas && <td className="py-2 pr-3 text-right font-mono tabular-nums">{d.cargas ?? 0}</td>}
                    {cfg.columnas.yaPagado && <td className="py-2 pr-3 text-right font-mono tabular-nums text-muted-foreground">{money(d.ya_pagado)}</td>}
                    <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(d.valor)}</td>
                    {cfg.columnas.cincoPct && <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d.valor_cargas)}</td>}
                    {cfg.columnas.cincoPct && <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(d.total)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {!actual && (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          No hay liquidación para {periodo}. Genérala para calcularla{cfg.tipo === 'decimo_tercero' ? ' (usa los roles cerrados del período)' : ''}.
        </CardContent></Card>
      )}
    </div>
  );
}

export function DecimoTercero() {
  return <Liquidacion cfg={{
    tipo: 'decimo_tercero',
    titulo: 'Décimo Tercer Sueldo',
    leyenda: 'Art. 111 CT: dozava parte de lo percibido de diciembre a noviembre; pago acumulado hasta el 24 de diciembre. Se calcula sobre los roles CERRADOS del período y descuenta lo ya recibido mensualizado. No aporta al IESS ni paga IR (Art. 112).',
    columnas: { base: 'Remuneraciones período', yaPagado: true },
  }} />;
}

export function DecimoCuarto() {
  return <Liquidacion cfg={{
    tipo: 'decimo_cuarto',
    titulo: 'Décimo Cuarto Sueldo',
    leyenda: 'Art. 113 CT: un SBU proporcional al tiempo trabajado y a la jornada. Período según la región configurada en Parámetros (Sierra/Amazonía: ago→jul, pago hasta 15-ago · Costa/Insular: mar→feb, pago hasta 15-mar). Descuenta lo ya recibido mensualizado.',
    columnas: { base: 'Valor teórico', dias: true, yaPagado: true },
  }} />;
}

export function Utilidades() {
  return <Liquidacion cfg={{
    tipo: 'utilidades',
    titulo: 'Participación de Utilidades',
    leyenda: 'Art. 97 CT: 15% de la utilidad líquida — 10% por tiempo trabajado en el año y 5% en proporción a las cargas familiares (cónyuge/conviviente, hijos menores de 18 y con discapacidad). Pago hasta el 15 de abril del año siguiente. Si nadie registra cargas, el 5% se reparte por tiempo.',
    conMonto: true,
    columnas: { dias: true, cargas: true, cincoPct: true },
  }} />;
}
